import React, { useState } from "react";
import { cancelInviteToken, getOrganizationPendingInvites } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { OrgTabHeader, OrgErrorPanel, OrgEmptyState, OrgRequestCard } from "./orgTabShell";
import { cn } from "../../lib/utils";

/**
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function PendingAffiliationsTab({ userId, user, refreshUser }) {
  const [error, setError] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // invite token
  const [loading, setLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getOrganizationPendingInvites(userId);
      if (cancelled) return;
      if (result.error || !result.success) {
        setError(result.error || "Failed to load pending invitations");
        setPendingInvites([]);
        return;
      }
      setPendingInvites(result.data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, user, refreshUser]);

  const displayName = (inv) => inv?.email || "Invited user";

  const initials = (name) => name.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase() || "U";

  /**
   * @param {Object} affiliation
   * @returns {Promise<void>}
   */
  const handleCancelInvitation = async (invite) => {
    setLoading(true);
    setError(null);
    try {
      const result = await cancelInviteToken(invite.token);

      if (result.error) {
        setError(result.error || "Failed to cancel invitation");
      } else {
        setConfirmCancel(null);
        setPendingInvites((prev) => prev.filter((x) => x.token !== invite.token));
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (err) {
      setError("Network error while canceling invitation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <OrgTabHeader
        title="Pending affiliations"
        description="Open invitations you have sent. You can cancel an invitation before it is accepted."
      />

      {error ? <OrgErrorPanel message={error} /> : null}

      {pendingInvites.length === 0 ? (
        <OrgEmptyState title="No pending invitations" subtitle="When you send invites, they will show here." />
      ) : (
        <ul className="list-none space-y-4 p-0">
          {pendingInvites.map((invite) => {
            const name = displayName(invite);
            const pfp = defaultPfpUrl;

            return (
              <li key={invite.token}>
                <OrgRequestCard>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                      <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
                        <AvatarImage
                          src={pfp}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = defaultPfpUrl;
                          }}
                        />
                        <AvatarFallback className="text-text-secondary">{initials(name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-lg font-semibold text-accent">{name}</span>
                          <Badge variant="secondary" className="font-normal capitalize">
                            pending invite
                          </Badge>
                        </div>
                        {invite.role ? (
                          <p className="text-sm text-text-secondary">
                            Role: <span className="font-medium text-text-primary">{invite.role}</span>
                          </p>
                        ) : null}
                        {invite.organization_name ? (
                          <p className="text-sm leading-relaxed text-text-secondary">
                            Organization: {invite.organization_name}
                          </p>
                        ) : null}
                        {invite.created_at ? (
                          <p className="text-sm text-text-muted">
                            Invited {new Date(invite.created_at).toLocaleDateString()}
                          </p>
                        ) : null}
                        {invite.expires_at ? (
                          <p className="text-sm text-text-muted">
                            Expires {new Date(invite.expires_at).toLocaleDateString()}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 sm:ml-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={loading}
                        onClick={() => setConfirmCancel(invite)}
                        className={cn("border-delete text-delete hover:bg-ui-muted hover:text-delete")}
                      >
                        Cancel invitation
                      </Button>
                    </div>
                  </div>
                </OrgRequestCard>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={!!confirmCancel}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmCancel(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel invitation?</DialogTitle>
            <DialogDescription>
              {confirmCancel
                ? `This will cancel the pending invitation to ${displayName(confirmCancel)}. They will no longer see it.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmCancel(null)}
              disabled={loading}
            >
              Keep invitation
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => confirmCancel && handleCancelInvitation(confirmCancel)}
              disabled={loading}
            >
              {loading ? "Canceling…" : "Yes, cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PendingAffiliationsTab;
